import { Dialog as BaseDialog, Transition } from "@headlessui/react";
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";
import {
  DialogScrollState,
  FullPageDialogContext,
} from "../../contexts/FullPageDialogContext";

import { Icon, H5 } from "@sellar-io/components";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { PageIndicator } from "../pageIndicator/PageIndicator";
import { FullPageDialogLeaveTime } from "./dialogConstants";
import { useFullPageDialogContext } from "./useFullPageDialogContext";

export const DialogRoot = ({
  active,
  children,
  onDismiss,
  afterLeave,
  className,
  initPage,
  fullHeight,
  dialogBackdropClassName,
  dialogClassName,
  onPageChange,
}: {
  active: boolean;
  onDismiss?: () => void;
  afterLeave?: () => void;
  children: ReactNode;
  className?: string;
  initPage?: number;
  fullHeight?: boolean;
  dialogBackdropClassName?: string;
  dialogClassName?: string;
  onPageChange?: (pageNum: number | undefined) => void;
}) => {
  const [page, setPage] = useState(initPage);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<DialogScrollState>({
    hasOverflow: false,
    isAtTop: true,
    isAtBottom: true,
  });

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  // Reset page while closed so reopening starts on initPage without an effect.
  if (!active && page !== initPage) {
    setPage(initPage);
  }

  useEffect(() => {
    if (onPageChange) {
      onPageChange(page);
    }
  }, [onPageChange, page]);

  return (
    <Transition show={active} as={Fragment} afterLeave={afterLeave}>
      <BaseDialog onClose={() => onDismiss?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={`ease-in duration-${FullPageDialogLeaveTime}`}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={twMerge(
              `fixed inset-0 ${zLayer.dialog} bg-black/30`,
              dialogBackdropClassName,
            )}
            aria-hidden="true"
          />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <div
            className={twMerge(
              `fixed inset-0 ${zLayer.dialog} flex items-center justify-center`,
              dialogClassName,
            )}
          >
            <div className="flex h-full w-full items-center justify-center">
              <BaseDialog.Panel
                className={twMerge(
                  "flex h-full w-screen max-w-[700px] flex-col overflow-hidden rounded-lg bg-white",
                  fullHeight ? "md:h-[85vh]" : "md:h-min md:max-h-[85vh]",
                  className,
                )}
              >
                <FullPageDialogContext.Provider
                  value={{
                    page,
                    setPage,
                    active,
                    onDismiss,
                    fullHeight: fullHeight ?? false,
                    scrollToTop,
                    scrollContainerRef,
                    scrollState,
                    setScrollState,
                  }}
                >
                  <div
                    ref={fullHeight ? undefined : scrollContainerRef}
                    className={twMerge(
                      "flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain",
                      fullHeight && "overflow-y-hidden",
                    )}
                  >
                    <div
                      className={twMerge(
                        "flex min-h-full flex-col",
                        fullHeight && "h-full min-h-0",
                      )}
                    >
                      <FloatingLayerProvider host="dialog">
                        {children}
                      </FloatingLayerProvider>
                    </div>
                  </div>
                </FullPageDialogContext.Provider>
              </BaseDialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </BaseDialog>
    </Transition>
  );
};

export const Navigation = ({
  leftIcon,
  onClickLeftIcon,
  title,
  description,
  onClickTitle,
  children,
  className,
}: {
  leftIcon?: keyof typeof IconType;
  onClickLeftIcon?: () => void;
  title?: string;
  description?: string;
  onClickTitle?: () => void;
  children?: ReactNode;
  className?: string;
  strikethrough?: string;
}) => {
  const [ref, isIntersecting] = useIntersectionObserver({});
  const { fullHeight, scrollState } = useFullPageDialogContext();
  const showShadow = fullHeight
    ? scrollState.hasOverflow && !scrollState.isAtTop
    : !isIntersecting;

  return (
    <>
      <div ref={ref} />
      <div
        className={twMerge(
          "sticky top-0 z-30 flex shrink-0 flex-col gap-4 bg-white p-4",
          showShadow && "shadow-lg",
          className,
        )}
      >
        <div
          className={
            "grid grid-cols-[24px_1fr_24px] content-center items-center justify-items-center"
          }
        >
          {leftIcon && onClickLeftIcon && leftIcon !== "MENU" ? (
            <div onClick={onClickLeftIcon}>
              <Icon size={1} icon={leftIcon} className={"cursor-pointer"} />
            </div>
          ) : (
            <div />
          )}
          {title ? (
            <H5
              className={twMerge(
                "text-center text-lg",
                onClickTitle && "cursor-pointer",
              )}
              onClick={onClickTitle}
            >
              <BaseDialog.Title as={"span"}>{title}</BaseDialog.Title>
            </H5>
          ) : (
            <div />
          )}
          <div />
        </div>
        {description ? (
          <div className={"text-center text-gray-500"}>
            <BaseDialog.Description>{description}</BaseDialog.Description>
          </div>
        ) : (
          <div />
        )}
        {children}
      </div>
    </>
  );
};

export const Content = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { fullHeight, scrollContainerRef, setScrollState } =
    useFullPageDialogContext();

  const updateScrollState = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const hasOverflow =
      scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
    setScrollState({
      hasOverflow,
      isAtTop: scrollContainer.scrollTop <= 1,
      isAtBottom:
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 1,
    });
  }, [scrollContainerRef, setScrollState]);

  useEffect(() => {
    if (!fullHeight) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    updateScrollState();
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(scrollContainer);
    Array.from(scrollContainer.children).forEach((child) => {
      resizeObserver.observe(child);
    });
    const mutationObserver = new MutationObserver(updateScrollState);
    mutationObserver.observe(scrollContainer, {
      childList: true,
      subtree: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [fullHeight, scrollContainerRef, updateScrollState]);

  return (
    <div
      ref={fullHeight ? scrollContainerRef : undefined}
      onScroll={fullHeight ? updateScrollState : undefined}
      className={twMerge(
        "shrink-0 px-4 pb-4",
        fullHeight &&
          "min-h-0 flex-1 shrink overflow-x-hidden overflow-y-auto overscroll-contain",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const NewFeatureHeader = ({
  imageSrc,
  onClickLeftIcon,
}: {
  imageSrc: string;
  onClickLeftIcon?: () => void;
}) => {
  return (
    <div className={"bg-zinc-100 px-8"}>
      {onClickLeftIcon && (
        <Icon
          size={1}
          className={"mt-4 -ml-4 cursor-pointer"}
          icon={"CHEVRON_DOWN"}
          onClick={onClickLeftIcon}
        />
      )}
      <div className={"flex items-center justify-center bg-zinc-100"}>
        <img src={imageSrc} className={"mt-auto aspect-auto"} />
      </div>
    </div>
  );
};

export const NewFeatureContent = ({
  title,
  featureHeader,
  description,
}: {
  title: string;
  featureHeader?: string;
  description: string;
}) => {
  const featureHeaderText = featureHeader ?? "NEW FEATURE";
  return (
    <div className={"flex flex-col p-8"}>
      <div>
        <div
          className={
            "inline-flex items-center gap-2 rounded-sm bg-purple-100 px-2 py-1 text-sm font-semibold"
          }
        >
          <p className={"text-sm text-[#B557F1]"}>{featureHeaderText}</p>
        </div>
      </div>
      <H5 className={"mt-4"}>{title}</H5>
      <p className={"mt-2"}>{description}</p>
    </div>
  );
};

export const NewFeaturePageIndicator = ({
  pageLength,
  activePageIndex,
}: {
  pageLength: number;
  activePageIndex: number;
}) => {
  return (
    <div className="mt-8 flex flex-row items-center justify-center gap-2">
      <PageIndicator
        pageLength={pageLength}
        activePageIndex={activePageIndex}
      />
    </div>
  );
};

export const CTA = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { fullHeight, scrollState } = useFullPageDialogContext();
  const showShadow =
    fullHeight && scrollState.hasOverflow && !scrollState.isAtBottom;

  return (
    <>
      <div
        className={twMerge(
          "relative mt-auto flex w-full items-end justify-end space-x-3 p-4 pt-3",
          fullHeight && "sticky bottom-0 z-30 shrink-0 bg-white",
          showShadow && "shadow-[0_-8px_14px_rgba(0,0,0,0.12)]",
          className,
        )}
      >
        {showShadow ? (
          <div className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-linear-to-t from-white to-transparent" />
        ) : null}
        {children}
      </div>
    </>
  );
};

export const Page = ({
  page,
  children,
}: {
  page: number;
  children: ReactNode;
}) => {
  const { page: currentPage } = useFullPageDialogContext();

  if (page !== currentPage) return null;

  return <>{children}</>;
};

export {
  DialogRoot as Dialog,
  Navigation as DialogNavigation,
  Content as DialogContent,
  CTA as DialogCTA,
  Page as DialogPage,
  NewFeatureHeader as DialogNewFeatureHeader,
  NewFeatureContent as DialogNewFeatureContent,
  NewFeaturePageIndicator as DialogNewFeaturePageIndicator,
};
